import { getLogger } from '@logtape/logtape';
import { useEffect } from 'react';
import useSWRImmutable from 'swr/immutable';

import useConfig from '@/hooks/useConfig.ts';

import { PractitionerDataCurDto, PractitionerDocCirrusDto } from '@/models/PractitionerDto.ts';
import { CalendarInfo, EnrichedPractitionerData } from '@/models/appointment/EnrichedPractitionerData.ts';
import { ProfessionalTypeDocument, ServiceOrTreatmentDocument } from '@/models/prismicTypes.ts';

import {
    fetchPractitionerByCustomerId,
    fetchProfessionalTypesByCustomerId,
    fetchServiceOrTreatmentTypesByCustomerId,
} from '@/services/appointmentService.ts';

import { DEFAULT_SELECTED_CALENDARS, useFilterStore } from '@/stores/filterStore.ts';

import { sortPractitionerCalendars } from '@/utils/calendarUtils.ts';
import { enrichPractitionerCachedData } from '@/utils/practitioner/cachedDataUtils.ts';
import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';

const logger = getLogger(['hook', 'enriched-practitioner-data']);

interface EnrichedPractitionerDataHook {
    data?: EnrichedPractitionerData[];
    calendars: CalendarInfo[];
    is_loading: boolean;
    appointmentTypeMap: Record<string, string>;
    practiceLabelMap: Record<string, string>;
}

interface ProcessedPractitionerData {
    practitioners: EnrichedPractitionerData[];
    calendars: CalendarInfo[];
}

interface EnrichedPractitionerDataForInstance {
    data?: EnrichedPractitionerData;
    is_loading: boolean;
}

export const useEnrichedPractitionerDataForSpecificInstance = (
    instance_id: string
): EnrichedPractitionerDataForInstance => {
    const { data, is_loading } = useEnrichedPractitionerData();

    // Find the data for the specific instance
    const filtered_data = data?.find((item) => item.instance_id === instance_id);

    return {
        data: filtered_data,
        is_loading: is_loading,
    };
};

const buildAppointmentTypeMap = (data: EnrichedPractitionerData[]): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const instance of data) {
        for (const type of instance.appointment_types ?? []) {
            if (!(type.id in map)) {
                map[type.id] = type.name;
            }
        }
    }
    return map;
};

const buildPracticeLabelMap = (data: EnrichedPractitionerData[]): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const instance of data) {
        for (const practice of instance.practices ?? []) {
            if (practice.pvs?.id && !(practice.pvs.id in map)) {
                map[practice.pvs.id] = practice.pvs.name ?? practice.pvs.id;
            }
        }
    }
    return map;
};

// Issue = we don't have a prismic mapping for practices... (practices)
export const useEnrichedPractitionerData = (): EnrichedPractitionerDataHook => {
    const { customer_id, instance_ids, location_key } = useConfig();
    const cache_key = {
        key: 'practitioner-cached-data',
        customer_id: customer_id,
        instance_ids: instance_ids,
    };
    const selected_practitioners = useFilterStore((state) => state.selected_practitioners);
    const selected_calendars = useFilterStore((state) => state.selected_calendars);

    const fetchPractitionerData = async (): Promise<ProcessedPractitionerData> => {
        try {
            // Fetch all practitioner data and professional types by customer_id from Cache API
            const practitioner_data_promise = fetchPractitionerByCustomerId(customer_id);
            const professional_types_promise = fetchProfessionalTypesByCustomerId(customer_id);
            const service_or_treatment_types_promise = fetchServiceOrTreatmentTypesByCustomerId(customer_id);
            const [{ linked_dc_users, linked_data_cur_users }, raw_professional_types, raw_service_or_treatment_types] =
                await Promise.all([
                    practitioner_data_promise,
                    professional_types_promise,
                    service_or_treatment_types_promise,
                ]);

            // Convert raw_professional_types to a map
            const map_professional_type: Map<string, ProfessionalTypeDocument> = new Map(
                raw_professional_types.map((type) => [type.id, type])
            );

            // Convert raw_service_or_treatment_types to a map
            // used for data cur where the appointment type name is stored only in prismic
            const map_service_or_treatment_type: Map<string, ServiceOrTreatmentDocument> = new Map(
                raw_service_or_treatment_types.map((type) => [getTextFromRichTextField(type.data.key), type])
            );

            const instance_id_set = new Set<string>(instance_ids);

            // Filter all pvs users by instance_ids
            const linked_doc_cirrus_user_filtered_by_instance_ids: PractitionerDocCirrusDto[] = linked_dc_users.filter(
                (user) => instance_id_set.has(user.dc_user_mapping.dc_instance_id)
            );
            const linked_data_cur_user_filtered_by_instance_ids: PractitionerDataCurDto[] =
                linked_data_cur_users.filter((user) => instance_id_set.has(user.data_cur_user_mapping.dc_instance_id));

            // Enrich practitioner data
            const enriched_doc_cirrus_practitioner: EnrichedPractitionerData = enrichPractitionerCachedData(
                linked_doc_cirrus_user_filtered_by_instance_ids,
                customer_id,
                map_professional_type
            );
            const enriched_data_cur_practitioner: EnrichedPractitionerData = enrichPractitionerCachedData(
                linked_data_cur_user_filtered_by_instance_ids,
                customer_id,
                map_professional_type,
                map_service_or_treatment_type
            );

            const practitioners = [enriched_doc_cirrus_practitioner, enriched_data_cur_practitioner];

            const sorted_calendars = sortPractitionerCalendars(practitioners);

            const final_result = {
                practitioners: practitioners,
                calendars: sorted_calendars,
            };

            return final_result;
        } catch (error) {
            logger.error('Failed to build enriched practitioner data', { error: error });
            return {
                practitioners: [],
                calendars: [],
            };
        }
    };

    const { data: processed_practitioner_data, isLoading: is_loading } = useSWRImmutable<ProcessedPractitionerData>(
        cache_key,
        fetchPractitionerData
    );

    const data = processed_practitioner_data?.practitioners;

    const appointmentTypeMap = data ? buildAppointmentTypeMap(data) : {};
    const practiceLabelMap = data ? buildPracticeLabelMap(data) : {};

    // Initialize practitioners only when data is first loaded, filtered by current location
    useEffect(() => {
        if (data?.some((item) => !item.practitioners) || selected_practitioners[location_key]) return;
        if (!data) return;

        // Filter practitioners by current location
        const practitioner_ids = data.flatMap((instance_data) =>
            instance_data.practitioners
                .filter((prac) => prac.practices.some((practice) => practice.prismic_hub.key === location_key))
                .map((prac) => prac.id)
        );

        useFilterStore.setState((state) => ({
            selected_practitioners: {
                ...state.selected_practitioners,
                [location_key]: practitioner_ids,
            },
        }));
    }, [data, location_key, selected_practitioners]);

    useEffect(() => {
        if (!data || data.some((item) => !item.practitioners) || selected_calendars[location_key]) {
            return;
        }

        // Extract calendar IDs only from practitioners at the current location
        const all_calendar_ids = [
            ...new Set(
                data.flatMap((instance_data) =>
                    instance_data.practitioners
                        .filter((prac) => prac.practices.some((practice) => practice.prismic_hub.key === location_key))
                        .flatMap((practitioner) => practitioner.calendars.map((calendar) => calendar.pvs.id))
                )
            ),
        ];

        // Auto-select only first 3 calendars
        const selected_calendar_ids = all_calendar_ids.slice(0, DEFAULT_SELECTED_CALENDARS);

        useFilterStore.setState((state) => ({
            selected_calendars: {
                ...state.selected_calendars,
                [location_key]: selected_calendar_ids,
            },
        }));
    }, [data, location_key, selected_calendars]);

    return {
        data: data,
        calendars: processed_practitioner_data?.calendars ?? [],
        is_loading: is_loading,
        appointmentTypeMap: appointmentTypeMap,
        practiceLabelMap: practiceLabelMap,
    };
};
